import React, {Component} from 'react';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Switch from '@material-ui/core/Switch';

/**
 * Mockup screen S3 — "Effective access" (docs/mockups/access-control-mockups.html; slice 4b-2 §3.2,
 * §3.3). The screen that lets support answer an access question without a developer and a SQL
 * prompt.
 *
 * REPLACES a 26-line flat list of permission codes. That version could not answer either question
 * the screen exists for: it showed neither WHERE a permission applies, nor WHICH grant produced it,
 * nor anything at all about what the person cannot do.
 */

/** `schema · BENEFIT`, `object · BENEFIT.PENSION_CALC`, `global`. Never a uuid — §3.10. */
const scopeText = (s) => (s.type === 'global' ? 'global' : `${s.type} · ${s.label}`);

/**
 * The "Comes from" cell. `source` says how the grant was CREATED and the group flag says who HOLDS
 * it; reading one to answer the other is the wrong-cell bug the audit panel shipped in #705.
 *
 * @param {object} v One `via` entry.
 * @return {string} Rendered provenance.
 */
const comesFrom = (v) => {
  const what = v.roleCode || v.permissionCode || '';
  if (v.isGroup) return `${what} · Group · ${v.groupName || v.groupId}`;
  if (v.source === 'delegation') return `${what} · Delegation ← ${v.delegatedFromName || v.delegatedFromId}`;
  if (v.source === 'ownership') return `${what} · Ownership`;
  if (v.source === 'idp') return `${what} · IdP claim`;
  if (v.source === 'backfill') return `${what} · Backfill`;
  // A single permission granted directly is a SHARE rather than a role — the mockup's
  // "Direct — shared". `permission_code IS NOT NULL` is what distinguishes it.
  return v.permissionCode ? `${what} · Direct — shared` : `${what} · Direct`;
};

const until = (v) => (v.expiresAt ? new Date(v.expiresAt).toLocaleDateString() : '—');

class EffectiveAccessPanel extends Component {
  constructor(props) {
    super(props);
    // §3.3: the default table is bounded to `is_sensitive` (26 of 127 codes) plus every
    // confers-nothing row. The rest is behind this toggle rather than absent, because "why is
    // report:read not listed" is a question, and a wall of 127 never-held permissions is not an
    // answer to any question at all.
    this.state = {showAll: false};
    this.toggleShowAll = this.toggleShowAll.bind(this);
  }

  toggleShowAll() {
    this.setState((s) => ({showAll: !s.showAll}));
  }

  render() {
    const canDo = this.props.canDo || [];
    const cannotDo = this.props.cannotDo || [];
    const visibleCannot = this.state.showAll ?
      cannotDo : cannotDo.filter((r) => r.isSensitive || r.kind === 'CONFERS_NOTHING');

    // The mockup's "Can do — 27 permissions across 3 scopes". Real data is denser (a steward
    // returns 140 rows on dev), so the counts carry the weight the drawn row count cannot.
    const scopeCount = new Set(canDo.map((r) => `${r.scope.type}:${r.scope.id}`)).size;
    const codeCount = new Set(canDo.map((r) => r.code)).size;

    return (
      <div className="rounded-card effective-panel" data-testid="ac-effective-panel">
        <h3>
          Can do
          <span className="panel-count" data-testid="ac-effective-count">
            {` ${codeCount} permission${codeCount === 1 ? '' : 's'} across ${scopeCount} scope${scopeCount === 1 ? '' : 's'}`}
          </span>
        </h3>

        {!canDo.length && <div className="empty-hint">No effective permissions.</div>}
        {!!canDo.length && (
          <Table size="small" className="data-table">
            <TableHead>
              <TableRow>
                <TableCell><strong>Permission</strong></TableCell>
                <TableCell><strong>Where</strong></TableCell>
                <TableCell><strong>Comes from</strong></TableCell>
                <TableCell><strong>Until</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {canDo.map((r) => (
                <TableRow key={`${r.code}|${r.scope.type}:${r.scope.id}`} hover data-testid="ac-effective-row">
                  <TableCell>
                    {r.isSensitive && <span className="sensitive-mark" title="Sensitive">🔒 </span>}
                    {r.code}
                  </TableCell>
                  <TableCell>{scopeText(r.scope)}</TableCell>
                  {/* ALL sources, not the first. A pair held by ownership AND through a group is
                      two true answers to "why can they do that"; picking one is picking half. */}
                  <TableCell>{r.via.map(comesFrom).join('; ') || '—'}</TableCell>
                  <TableCell>{r.via.map(until).join('; ')}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        <h3 className="cannot-do-heading">Cannot do — and why</h3>
        <FormControlLabel
          className="show-all-toggle"
          control={
            <Switch
              checked={this.state.showAll}
              onChange={this.toggleShowAll}
              inputProps={{'data-testid': 'ac-cannot-show-all'}}
            />
          }
          label="Include non-sensitive permissions"
        />

        {!visibleCannot.length && <div className="empty-hint">Nothing to report.</div>}
        {!!visibleCannot.length && (
          <Table size="small" className="data-table">
            <TableHead>
              <TableRow>
                <TableCell><strong>Permission</strong></TableCell>
                <TableCell><strong>Where</strong></TableCell>
                <TableCell><strong>Reason</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleCannot.map((r) => (
                <TableRow
                  key={`${r.kind}|${r.code}`}
                  hover
                  // The row this whole panel exists to surface gets its own hook and its own
                  // styling: a live grant that silently confers nothing.
                  className={r.kind === 'CONFERS_NOTHING' ? 'confers-nothing' : ''}
                  data-testid={r.kind === 'CONFERS_NOTHING' ? 'ac-confers-nothing-row' : 'ac-cannot-row'}
                >
                  <TableCell>
                    {r.isSensitive && <span className="sensitive-mark" title="Sensitive">🔒 </span>}
                    {r.code}
                  </TableCell>
                  {/* Generator 1 groups its schemas under the permission (§3.3). One row per
                      (permission, schema) reads fine against the mockup's three schemas and
                      becomes tens of rows on a real deployment. */}
                  <TableCell>{r.scopes.map(scopeText).join(', ')}</TableCell>
                  <TableCell>{r.reason}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    );
  }
}

export default EffectiveAccessPanel;
